import objectAssignDeep from 'object-assign-deep'
import hash from '~/utils/hash'
import { platformGetters } from '~/helpers/store'
import { getChangedAttributes, joinArrayBySeparator, transformObjectByKeys } from '~/helpers'
import { defaultState, SEARCHES } from '../constants'


export default {
  ...platformGetters,

  noteFilters: s => s.filters,

  defaultFilters: () => ({ ...defaultState.filters }),

  changedFilters: (_, getters) => {
    const filters = getters.noteFilters
    const defaultFilters = getters.defaultFilters
    return getChangedAttributes(filters, defaultFilters)
  },

  apiReadyNoteFilters: (_, getters) => () => {
    const updatedFilters = getChangedAttributes(
      getters.noteFilters,
      defaultState.filters
    )

    const apiFilter = transformObjectByKeys(updatedFilters, {
      categories: categories => ({ contentCategoryId: joinArrayBySeparator(categories) }),
      venueId: venueId => ({ venueId: joinArrayBySeparator(venueId) }),
      type: type => ({ type })
    })

    return { filter: apiFilter }
  },

  notesBySearch: ({ meta }, getters) => params => {
    delete params.page
    const withNoteFilters = getters.applyNoteFilters({ params })
    const hashedSearch = hash(withNoteFilters)
    const thisSearch = meta.searches[hashedSearch]
    if (!thisSearch) {
      return []
    }

    return thisSearch.pages.reduce((acc, thisPage) => [
      ...acc,
      ...thisPage.map(noteId => getters.resourceById(noteId))
    ], [])
  },

  applyNoteFilters: (s, getters) => ({ params }) => {
    if (params && params.filter && params.filter.isFeatured) {
      return objectAssignDeep({}, params)
    }
    const noteFilters = getters.apiReadyNoteFilters()
    return objectAssignDeep({}, noteFilters, params)
  },

  getSearch: (_, getters) => params => {
    const withFilters = getters.applyNoteFilters({ params })
    const search = getters.cachedSearch(withFilters)
    return search
  },

  getFetching: (_, getters) => params => {
    const search = getters.getSearch(params)
    return search ? search.fetching : false
  },

  getFailed: (_, getters) => params => {
    const search = getters.getSearch(params)
    return search ? search.failed : false
  },

  cachedSearch: ({ meta }) => params => {
    const hashedSearch = hash(params)
    return meta.searches[hashedSearch]
  },

  isSearchCached: ({ meta }) => params => {
    const hashedSearch = hash(params)

    return typeof meta.searches[hashedSearch] !== 'undefined'
  },

  isSearchEmpty: ({ meta }, getters) => params => {
    const hashedSearch = hash(params)

    return getters.isSearchCached(params) && meta.searches[hashedSearch].pages[0].length === 0
  },

  isSearchComplete: (_, getters) => params => {
    if (getters.isSearchCached(params)) {
      const search = getters.cachedSearch(params)
      return search.pages.length === search.totalPages
    }
    return false
  },

  allNotesFetched: (_, getters) => {
    const withNoteFilters = getters.applyNoteFilters({ params: SEARCHES.ALL })
    return getters.isSearchComplete(withNoteFilters)
  },

  areFiltersApplied: (_, getters) => {
    const changedFilters = getters.changedFilters
    return Object.keys(changedFilters).length > 0
  },

  queryStringFilters: (_, getters) => {
    const filters = getters.noteFilters
    const qsFilters = getChangedAttributes(filters, defaultState.filters)

    const query = Object.entries(qsFilters).reduce((acc, [key, val]) => ({
      ...acc,
      [key]: joinArrayBySeparator(val)
    }), {})

    return query
  },

  noteById: (s, getters) => id =>
    getters.resourceById(id) ? getters.resourceById(id) : null,

  featuredNotes: (_, getters) => getters.notesBySearch(SEARCHES.FEATURED),
  preferencedNotes: (_, getters) => getters.notesBySearch(SEARCHES.PREFERENCED),
  featuredFetching: (_, getters) => getters.getFetching(SEARCHES.FEATURED),
  preferencedFetching: (_, getters) => getters.getFetching(SEARCHES.PREFERENCED),
  featuredFailed: (_, getters) => getters.getFailed(SEARCHES.FEATURED),

  subscribedNotes: (_, getters) => getters.notesBySearch(SEARCHES.SUBSCRIBED),
  subscribedFetching: (_, getters) => getters.getFetching(SEARCHES.SUBSCRIBED),
  subscribedFailed: (_, getters) => getters.getFailed(SEARCHES.SUBSCRIBED),

  allNotes: (_, getters) => getters.notesBySearch(SEARCHES.ALL),
  allFetching: (_, getters) => getters.getFetching(SEARCHES.ALL),
  allFailed: (_, getters) => getters.getFailed(SEARCHES.ALL),

  latestNotes: (_, getters) => getters.notesBySearch(SEARCHES.LATEST),

  venueCityGuide: (_, getters) => id => {
    const params = { ...SEARCHES.CITYGUIDE }
    params.filter.venue_id = id
    return getters.notesBySearch(params)
  },
  venueCityGuideFetching: (_, getters) => id => {
    const params = { ...SEARCHES.CITYGUIDE }
    params.filter.venue_id = id
    return getters.getFetching(params)
  },
  venueCityGuideFetched: (_, getters) => id => {
    const params = { ...SEARCHES.CITYGUIDE }
    params.filter.venue_id = id
    const withNoteFilters = getters.applyNoteFilters({ params })
    return getters.isSearchComplete(withNoteFilters)
  },
  venueNotes: (_, getters) => id => {
    const params = { ...SEARCHES.VENUE }
    params.filter.venue_id = id
    return getters.notesBySearch(params)
  },
  venueNotesFetching: (_, getters) => id => {
    const params = { ...SEARCHES.VENUE }
    params.filter.venue_id = id
    return getters.getFetching(params)
  }
}
